import { memo, useEffect, useState } from "react";
import { useAppContext } from "../../../../../context/appContext";
import { useNavigate } from "react-router-dom";
import loginFormValidation from "../../LoginFunctions/loginFormValidation";

import FeedbackMessage from "./FeedbackMessage";
import LoginFields from "./LoginFields";
import authService from "../../../../../service/auth.service";
import { Typography } from "@mui/material";
import KsbActionButton from "../../../../../components/KsbActionButton";
import { Login } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

function LoginCard() {
  const { setCurrentUser, isAuthenticated } = useAppContext();
  const theme = useTheme();

  const navigate = useNavigate();

  // ✅ FORM STATE
  const [formState, setFormState] = useState({
    email: "",
    userPassword: "",
  });

  // ✅ ERRORS (aligned with signup)
  const [errors, setErrors] = useState({
    emailErr: {},
    passwordErr: {},
  });

  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ CHANGE HANDLER (same idea as signup)
  const handleChange = (field) => (e) => {
    const value = e.target.value;

    setMsg("");

    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));

    // clear errors per field
    setErrors((prev) => ({
      ...prev,
      [`${field === "userPassword" ? "passwordErr" : "emailErr"}`]: {},
    }));
  };

  // ✅ SUBMIT
  const submit = async () => {
    setMsg("");

    const { isValid, validationErrors } = loginFormValidation(
      formState.email,
      formState.userPassword,
    );

    if (!isValid) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);

      const response = await authService.login(
        formState.email,
        formState.userPassword,
      );

      if (response.status === 200) {
        setCurrentUser(authService.getCurrentUser());
      }
    } catch (error) {
      console.error("❌ LOGIN ERROR");

      // ✅ server error only (no field mapping)
      if (error.response?.data?.message) {
        setMsg(error.response.data.message);
      } else {
        setMsg("Login fehlgeschlagen");
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ redirect after login
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        borderTopRightRadius: "15px",
        borderBottomRightRadius: "15px",
        gap: "1em",
      }}
    >
      {/* ✅ TITLE */}
      <Typography variant="h3" mt={2}>
        Login
      </Typography>
      {/* ✅ FIELDS (now with inline errors) */}
      <LoginFields
        email={formState.email}
        onChangeEmail={handleChange("email")}
        emailErr={errors.emailErr}
        userPassword={formState.userPassword}
        onChangePassword={handleChange("userPassword")}
        passwordErr={errors.passwordErr}
        onSubmit={submit}
      />

      {/* ✅ BUTTON */}
      <KsbActionButton
        onClick={submit}
        icon={<Login />}
        label="Anmelden"
        variant="contained"
        loading={loading}
        sx={{
          bgcolor: theme.palette.primary.main,
          "&:hover": { bgcolor: theme.palette.primary.dark },
        }}
      />

      {/* ✅ GLOBAL MESSAGE (server only) */}
      <FeedbackMessage errors={[]} msg={msg} />
    </div>
  );
}

export default memo(LoginCard);
